/**
 * ShareLinkModal - Main modal for creating share links
 * Handles configuration, API calls, and result display
 */

import { devLog, devWarn, devError } from '../../utils/devLog';
import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  useToast,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { ShareConfig, CreateShortLinkResponse } from '../../types/share.types';
import { chartShareService } from '../../services/ChartShareService';
import { shortLinkApiService } from '../../services/ShortLinkApiService';
import { ShareConfigForm } from './ShareConfigForm';
import { ShareLinkResult } from './ShareLinkResult';
import { buildChartMetadataFromResponse, parseDateTimeFromBasicInfo, keysToSnakeCase } from '../../utils/shareHelpers';
import { buildShortLinkUrl } from '../../utils/urlHelpers';
import { globalEventStream, createEvent } from '../common/AstrologyChart/universal_architecture/core/EventStream';

interface ShareLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  chartData?: any; // Chart API response data
}

type ModalStep = 'config' | 'result';

export function ShareLinkModal({ isOpen, onClose, chartData }: ShareLinkModalProps) {
  const toast = useToast();

  const [config, setConfig] = useState<ShareConfig>(
    chartShareService.getDefaultShareConfig()
  );
  const [result, setResult] = useState<CreateShortLinkResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState<ModalStep>('config');

  const handleCreate = async () => {
    // Validation
    if (!chartData) {
      toast({
        title: 'No chart loaded',
        description: 'Please load or create a chart first.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!chartShareService.validateShareConfig(config)) {
      toast({
        title: 'Invalid configuration',
        description: 'At least one section must be visible.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setIsLoading(true);

      // Extract data from current chart
      const basicInfo = chartData.basic_info;

      // Build chart_metadata using helper (flat structure with timezone_offset)
      const chartMetadata = buildChartMetadataFromResponse(
        chartData,
        basicInfo.chart_name || basicInfo.place_name
      );

      // Parse datetime for jataka_request (local time without timezone)
      const datetime = parseDateTimeFromBasicInfo(
        basicInfo.date,
        basicInfo.time
      );

      // Build request
      const request = {
        jataka_request: {
          datetime,
          latitude: basicInfo.latitudeValue,
          longitude: basicInfo.longitudeValue,
          timezone_offset: chartMetadata.timezone_offset,
          place_name: basicInfo.place_name || '',
          altitude: parseFloat(basicInfo.altitude) || 0.0,
        },
        chart_metadata: chartMetadata,
        share_config: {
          fields: keysToSnakeCase(config.fields) as any,
          expires_in_days: config.expires_in_days,
        },
      };

      // Debug: Log request data
      devLog('🔍 [ShareLinkModal] Creating shortlink with request:', {
        datetime: request.jataka_request.datetime,
        timezone_offset: request.jataka_request.timezone_offset,
        basicInfo_timezone: basicInfo.timezone,
        calculationMeta_timezone_offset: chartData.calculation_metadata?.timezone_offset,
        chartMetadata_timezone_offset: chartMetadata.timezone_offset,
      });

      // Create share link
      const response = await shortLinkApiService.createShortLink(request);

      // Override full_url with correct frontend URL
      const correctedResponse = {
        ...response,
        full_url: buildShortLinkUrl(response.short_code),
      };

      // Save short_code to chart metadata (append to array)
      try {
        const multipleChartsContext = window.multipleChartsContext;
        if (multipleChartsContext && multipleChartsContext.activeChartId) {
          const activeChart = multipleChartsContext.getActiveChart();
          if (activeChart) {
            // Get existing shortLinks array or create new one
            const existingShortLinks = activeChart.metadata.shortLinks || [];

            // Add new short code if not already present
            if (!existingShortLinks.includes(response.short_code)) {
              multipleChartsContext.updateChart(multipleChartsContext.activeChartId, {
                metadata: {
                  ...activeChart.metadata,
                  shortLinks: [...existingShortLinks, response.short_code]
                }
              });
              devLog('💾 Short link saved to chart metadata:', response.short_code);
              devLog(`📊 Total short links for this chart: ${existingShortLinks.length + 1}`);
            } else {
              devLog('ℹ️ Short link already exists in metadata');
            }
          }
        }

        // Emit event with new share data (KISS - no need to refetch from backend)
        globalEventStream.emit(createEvent('chart.data.updated', 'share-link-modal', {
          newShare: {
            short_code: response.short_code,
            full_url: correctedResponse.full_url,
            created_at: response.created_at,
            expires_at: response.expires_at,
            access_count: 0,
            is_expired: false,
            chart_name: chartMetadata.name || chartMetadata.birth_place
          }
        }));
      } catch (error) {
        devWarn('⚠️ Failed to save short link to metadata:', error);
      }

      setResult(correctedResponse);
      setStep('result');

      toast({
        title: 'Link created!',
        description: 'Your shareable link has been created.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      devError('Failed to create share link:', error);

      // Extract error message
      let errorMessage = 'Unknown error occurred';
      if (error instanceof Error) {
        errorMessage = error.message;
      }

      // Check if this is a per-chart quota error
      const isQuotaError = errorMessage.includes('Maximum number of share links');

      toast({
        title: isQuotaError ? 'Quota Reached' : 'Failed to create link',
        description: isQuotaError
          ? 'You have reached the maximum number of share links for this chart (3). Delete one to create another.'
          : errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateAnother = () => {
    setStep('config');
    setResult(null);
    setConfig(chartShareService.getDefaultShareConfig());
  };

  const handleClose = () => {
    onClose();
    // Reset state after animation
    setTimeout(() => {
      setStep('config');
      setResult(null);
      setConfig(chartShareService.getDefaultShareConfig());
    }, 300);
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {step === 'config' ? 'Create Short Link' : 'Link Created!'}
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody pb={6}>
          {!chartData ? (
            <Alert status="warning">
              <AlertIcon />
              Please load or create a chart first before creating a share link.
            </Alert>
          ) : step === 'config' ? (
            <ShareConfigForm config={config} onChange={setConfig} chartData={chartData} />
          ) : (
            result && (
              <ShareLinkResult
                result={result}
                onCreateAnother={handleCreateAnother}
              />
            )
          )}
        </ModalBody>

        <ModalFooter>
          {!chartData ? (
            <Button onClick={handleClose} colorScheme="blue">
              Close
            </Button>
          ) : step === 'config' ? (
            <>
              <Button variant="ghost" onClick={handleClose} mr={3}>
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                onClick={handleCreate}
                isLoading={isLoading}
                loadingText="Creating..."
              >
                Create Link
              </Button>
            </>
          ) : (
            <Button onClick={handleClose} colorScheme="blue">
              Done
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
